import { FunctionComponent, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { faChevronDown } from "@fortawesome/pro-solid-svg-icons";

import { FeedPerson, FeedRunCard } from "@signalist/shared-types";

import { Icon } from "@/components";
import { GlobalSearchModal } from "@/components/GlobalSearchModal/GlobalSearchModal";
import {
	formatCurrentEmploymentDateRange,
	sortEmploymentsMostRecentFirst,
} from "@/components/IdentityCard/employmentHistory";
import { Organization, People } from "@/pages/Private/pages/Apollo/schema/get-people";
import { ensureHttpProtocol } from "@/utils/url";
import { ContactProspectDetail } from "@/pages/Private/pages/Contacts/schema/contact-detail";
import { ListPersonHeader } from "@/pages/Private/pages/List/pages/Unassigned/components/ListPersonHeader";
import { useCopyToClipboard } from "@/utils/useCopyToClipboard";
import { extractDomain } from "@/utils/extractDomain";
import { showGlobalToast } from "@/utils/showGlobalToast";

import { useFeedPendingAdds, useFeedPendingAddsContext } from "../../utils/useFeedPendingAdds";

import { PersonSwitchMenu } from "./PersonSwitchMenu";

/**
 * Stand-in prospect built from the card's own (lean) person while the full
 * detail loads — name/role/location render immediately, the icon strip and
 * score badge upgrade in place once `getContactProspectDetail` lands.
 */
const buildProspectPreview = (person: FeedPerson): People =>
	({
		id: person.prospectId ?? 0,
		name: person.name,
		title: person.role,
		headline: person.headline,
		image: person.avatarUrl,
		linkedinUrl: person.linkedinUrl,
		email: person.email,
		phone: person.phone,
		country: person.location,
	}) as unknown as People;

interface FeedPersonIdentityProps {
	card: FeedRunCard;
	person: FeedPerson;
	/** Full prospect detail once fetched; undefined while loading / no prospect. */
	detail?: ContactProspectDetail;
	/** True while the Feed itself is fetching — an add must not read that fetch as its answer. */
	isFeedFetching?: boolean;
	onSelectPerson: (listPersonId: number) => void;
}

/**
 * Person box of a run card: the shared lead-panel identity card
 * ({@link ListPersonHeader}) fed by the prospect detail, plus the feed-only
 * "Switch" person switcher (D5, always visible — PREC-5041) as the card's
 * header action.
 */
export const FeedPersonIdentity: FunctionComponent<FeedPersonIdentityProps> = ({
	card,
	person,
	detail,
	isFeedFetching = false,
	onSelectPerson,
}) => {
	const { t } = useTranslation();
	// The Feed page owns the add-lead answers and in-flight guard (PREC-5041 review S1/S3), so
	// they survive the refetch that swaps the cards for a spinner. Rendered on its own (a card
	// host without the Feed), the card falls back to an owner of its own.
	const pagePendingAdds = useFeedPendingAddsContext();
	const localPendingAdds = useFeedPendingAdds({
		cards: [card],
		selectedListIds: [card.listId],
		isFeedFetching,
	});
	const pendingAdds = pagePendingAdds ?? localPendingAdds;
	const switchRef = useRef<HTMLButtonElement>(null);
	// Non-null while the menu is open; a new id each open so an older add's callback cannot
	// close a menu the user opened later (PREC-5041 review S3).
	const [menuInteractionId, setMenuInteractionId] = useState<number | null>(null);
	const [showSearchPerson, setShowSearchPerson] = useState(false);
	const [showProspectImage, setShowProspectImage] = useState(true);
	const { handleCopyClick, renderSnackbar } = useCopyToClipboard();

	const closeMenu = (interactionId: number) => {
		setMenuInteractionId((current) => (current === interactionId ? null : current));
	};

	const prospect = (detail as unknown as People) ?? buildProspectPreview(person);
	const organization = (detail?.organization ?? undefined) as unknown as Organization | undefined;
	const employments = sortEmploymentsMostRecentFirst(detail?.prospectEmployments ?? []);
	const companyUrl = organization?.websiteUrl || organization?.primaryDomain;

	// The "Add Person" search is launched company-scoped exactly like the Profiles tab's:
	// its filters tab opens with this organization's domain pre-applied, adding to this list.
	const companyName = organization?.name || card.company.name || "";
	const companyDomain =
		organization?.primaryDomain ||
		extractDomain(organization?.websiteUrl || card.company.websiteUrl);
	const initialFilterCompany = companyDomain
		? { name: companyName, domain: companyDomain }
		: undefined;
	const targetList = card.listId ? { id: card.listId, name: card.listName ?? "" } : undefined;

	return (
		<div className="flex min-w-0 flex-1 flex-col" data-testid="feed-person-box">
			<ListPersonHeader
				className="flex-1"
				currentCompanyUrl={companyUrl ? ensureHttpProtocol(companyUrl) : undefined}
				currentEmploymentDateRange={formatCurrentEmploymentDateRange(employments)}
				domain={person.prospectId ? "contact" : undefined}
				employments={employments}
				handleCopyClick={handleCopyClick}
				headerAction={
					<>
						<button
							ref={switchRef}
							className="flex shrink-0 cursor-pointer items-center gap-1 rounded-md border border-border-light px-1.5 py-0.5 text-[11px] font-semibold text-text-secondary hover:border-brand-light hover:text-brand-light"
							data-feed-noselect="true"
							data-testid="feed-person-switch-trigger"
							type="button"
							onClick={() =>
								setMenuInteractionId(
									menuInteractionId === null ? pendingAdds.nextInteractionId() : null
								)
							}
						>
							{t("feed.runCard.switchPerson")}
							<Icon className="h-2.5 w-2.5" icon={faChevronDown} />
						</button>

						{menuInteractionId !== null && (
							<PersonSwitchMenu
								anchorEl={switchRef.current}
								card={card}
								interactionId={menuInteractionId}
								listPersonId={person.listPersonId}
								pendingAdds={pendingAdds}
								people={card.people}
								selectedListPersonId={person.listPersonId}
								onAddError={() => showGlobalToast(t("feed.runCard.switchMenu.addError"))}
								onAddPerson={() => setShowSearchPerson(true)}
								onClose={closeMenu}
								onSelect={onSelectPerson}
							/>
						)}
					</>
				}
				organization={organization}
				prospect={prospect}
				setShowProspectImage={setShowProspectImage}
				showProspectImage={showProspectImage}
			/>
			{showSearchPerson && (
				<GlobalSearchModal
					suppressLeadPanel
					initialFilterCompany={initialFilterCompany}
					isOpened={showSearchPerson}
					setIsOpened={setShowSearchPerson}
					targetList={targetList}
					onAddToListSuccess={(addedCount) => {
						pendingAdds.startBulkRefresh();
						// The modal owns the toast but unmounts on close (list-scoped mode), so the
						// host says the same sentence — it outlives the modal (PREC-5041 review S2).
						showGlobalToast(
							t("contacts.addedToList", { count: addedCount, name: targetList?.name ?? "" }),
							6000,
							"success"
						);
					}}
				/>
			)}
			{renderSnackbar()}
		</div>
	);
};
